import { useEffect } from 'react';
import { useInfiniteQuery, useQuery, useQueryClient } from '@tanstack/react-query';
import { fetchActivities, fetchRecentActivities, PAGE_SIZE } from '@/api/activities';
import type { ActivityFilterCategory, ActivityRow } from '@/api/activities';
import { supabase, uniqueChannelTopic } from '@/lib/supabase';
import { useAuth } from '@/contexts/AuthContext';

/**
 * Paginated activities list with infinite scrolling.
 */
export function useActivities(
  filterCategory: ActivityFilterCategory = 'all',
  groupId?: string,
  scopeGroupIds?: string[],
) {
  return useInfiniteQuery<ActivityRow[]>({
    queryKey: ['activities', filterCategory, groupId, scopeGroupIds],
    queryFn: ({ pageParam }) =>
      fetchActivities({
        filterCategory,
        groupId,
        limit: PAGE_SIZE,
        offset: pageParam as number,
        scopeGroupIds,
      }),
    // Wait until the non-archived group list is known (for the cross-group feed)
    // so archived-group activities are never shown before scoping is applied.
    enabled: groupId !== undefined || scopeGroupIds !== undefined,
    initialPageParam: 0,
    getNextPageParam: (lastPage, allPages) => {
      if (lastPage.length < PAGE_SIZE) return undefined;
      return allPages.length * PAGE_SIZE;
    },
  });
}

/**
 * Simple query for dashboard widget — last N activities, no pagination.
 */
export function useRecentActivities(limit = 10, scopeGroupIds?: string[]) {
  return useQuery<ActivityRow[]>({
    queryKey: ['activities', 'recent', limit, scopeGroupIds],
    queryFn: () => fetchRecentActivities(limit, scopeGroupIds),
    enabled: scopeGroupIds !== undefined,
  });
}

/**
 * Realtime subscription that invalidates activities queries on new inserts.
 */
export function useActivitiesRealtimeSubscription() {
  const queryClient = useQueryClient();
  const { user } = useAuth();

  useEffect(() => {
    if (!user) return;

    const channel = supabase
      .channel(uniqueChannelTopic(`activities-${user.id}`))
      .on(
        'postgres_changes',
        { event: 'INSERT', schema: 'public', table: 'activities' },
        () => {
          queryClient.invalidateQueries({ queryKey: ['activities'] });
        },
      )
      .subscribe();

    return () => {
      supabase.removeChannel(channel);
    };
  }, [user, queryClient]);
}
